import { Box } from '@primer/react';
import { ColoredCircle } from '@upright/ui/common/generic/components/colored-circle-for-legend';
import { themeGet } from '@upright/ui/common/generic/theme/custom-theme';
import styled from 'styled-components';
import { BouSpacingLevel, fluidVerticalPadding } from '../../../../../../generic/layout/bou-spacing';
import { bouToolCommonBoxPadding } from '../../../../../../generic/layout/layout-constants';
import { ScoreTypeRadioSelector } from '../../../../input/score-type-radio-selector';
import { useImpactChartContext, useImpactChartFontSizes } from '../context';
import { dataHasGroupfolios } from '../shared';
import { ImpactChartDataSeries } from '../types';
import { ValueChainsSwitch } from './value-chains-switch';

export function TopInputsRow() {
  const context = useImpactChartContext();
  const {
    scoreType,
    setScoreType,
    absoluteModeDisabled,
    showValueChains,
    setShowValueChains,
    hideValueChainsSwitch,
    data,
    staticMode,
    desktopMode,
    enableE2eTestIds,
    showLegend,
    scoreUnitSelectorPlacement: netScoreFormatSelectorPlacement,
  } = context;
  const { inputsText } = useImpactChartFontSizes();

  const showScoreTypeSelectors = setScoreType !== undefined;
  const showValueChainsSwitch =
    context.showValueChainsSwitch || (data.length === 1 && desktopMode && !hideValueChainsSwitch);

  const nothingToShowInTopRow = !showLegend && !showScoreTypeSelectors && !showValueChainsSwitch;

  if (staticMode || nothingToShowInTopRow) {
    return <></>;
  }

  const isAbsoluteScoreTypesHidden = dataHasGroupfolios(data);

  return (
    <Root
      style={{
        fontSize: inputsText,
        display: 'flex',
        justifyContent: 'space-between',
      }}
    >
      <LeftAligned>
        {showLegend && <Legend data={data} />}
        {netScoreFormatSelectorPlacement === 'left' && showScoreTypeSelectors && (
          <ScoreTypeRadioSelector
            scoreType={scoreType}
            setScoreType={setScoreType}
            absoluteDisabled={absoluteModeDisabled}
            isAbsoluteScoreTypesHidden={isAbsoluteScoreTypesHidden}
            enableE2eTestIds={enableE2eTestIds}
          />
        )}
      </LeftAligned>
      <RightAligned>
        {netScoreFormatSelectorPlacement !== 'left' && showScoreTypeSelectors && (
          <ScoreTypeRadioSelector
            scoreType={scoreType}
            setScoreType={setScoreType}
            absoluteDisabled={absoluteModeDisabled}
            isAbsoluteScoreTypesHidden={isAbsoluteScoreTypesHidden}
            enableE2eTestIds={enableE2eTestIds}
          />
        )}
        {showValueChainsSwitch && (
          <Switch>
            <ValueChainSwitchWrap>
              <ValueChainsSwitch showValueChains={showValueChains} setShowValueChains={setShowValueChains} />
            </ValueChainSwitchWrap>
          </Switch>
        )}
      </RightAligned>
    </Root>
  );
}

const LeftAligned = styled.div`
  display: flex;
`;
const RightAligned = styled.div`
  display: flex;
`;

const Legend = (props: { data: ImpactChartDataSeries[] }) => {
  return (
    <Box display="flex">
      {props.data.map(d => {
        return (
          <Box key={d.name} marginRight={2}>
            <Box marginRight={'10px'}>
              <ColoredCircle color={d.color} />
            </Box>
            {d.name}
          </Box>
        );
      })}
    </Box>
  );
};

const Root = styled.div`
  background-color: ${themeGet('colorSchemes.light.colors.canvas.subtle')};
  ${bouToolCommonBoxPadding};
  ${fluidVerticalPadding(BouSpacingLevel.TINY)};
`;

const ValueChainSwitchWrap = styled.div``;

const Switch = styled.div`
  display: flex;
  align-items: center;
  & + & {
    margin-left: 1.75em;
  }
`;
